import React, { useEffect, useMemo, useRef } from 'react';
import { Page } from '../types/storybook';
import {
  PreflightIssue,
  PreflightIssueCode,
  summarizePreflight
} from '../utils/pagePreflight';
import './PreflightPanel.css';

interface PreflightPanelProps {
  pages: Page[];
  issues: PreflightIssue[];
  onClose: () => void;
  onGoToIssue: (issue: PreflightIssue) => void;
  onFixIssue: (issue: PreflightIssue) => void | Promise<void>;
  onContinue?: () => void;
  onContinueToExportReview?: () => void;
  focusedPageId?: string | null;
  fixingIssueKey?: string | null;
}

interface PageIssueGroup {
  pageId: string;
  page: Page | undefined;
  pageIndex: number;
  issues: PreflightIssue[];
}

export const preflightIssueKey = (issue: PreflightIssue) => (
  `${issue.pageId}:${issue.elementId || 'page'}:${issue.code}`
);

const BROWSER_REVIEWABLE_EXPORT_CODES = new Set<PreflightIssueCode>([
  'ELEMENT_OUTSIDE_TRIM',
  'ELEMENT_CLIPPED',
  'TEXT_OUTSIDE_SAFE_AREA',
  'TEXT_TOO_SMALL',
  'TEXT_LOW_CONTRAST',
  'TEXT_COLLISION',
  'TEXT_SUBJECT_OVERLAP',
  'SEMANTIC_REGIONS_MISSING',
  'FULL_BLEED_COVERAGE_MISSING',
  'EDGE_BLEED_FIT_MISSING',
  'IMAGE_DPI_LOW',
  'IMAGE_DPI_UNKNOWN',
  // A browser measurement failure is not an override. It may advance only to
  // the authoritative server check, which must independently accept or block.
  'TEXT_MEASUREMENT_FAILED'
]);

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled])',
  '[href]',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/**
 * This browser check only controls whether the user may reach server review.
 * The server remains authoritative and default-denies every unknown code.
 */
export const canReviewBlockedPreflightForExport = (issues: PreflightIssue[]): boolean => {
  const errors = issues.filter(issue => issue.severity === 'error');
  return errors.length > 0
    && errors.every(issue => BROWSER_REVIEWABLE_EXPORT_CODES.has(issue.code));
};

const issueTargetLabel = (issue: PreflightIssue) => (
  issue.elementId ? `Element ${issue.elementId}` : 'Whole page'
);

const metricsLabel = (issue: PreflightIssue) => {
  const parts: string[] = [];
  if (issue.metrics?.contrastRatio !== undefined) {
    parts.push(`Contrast ${issue.metrics.contrastRatio.toFixed(2)}:1`);
  }
  if (issue.metrics?.effectiveDpi !== undefined) {
    parts.push(`Effective DPI ${Math.round(issue.metrics.effectiveDpi)}`);
  }
  if (issue.metrics?.naturalWidth && issue.metrics?.naturalHeight) {
    parts.push(`Source ${issue.metrics.naturalWidth}×${issue.metrics.naturalHeight}px`);
  }
  return parts.join(' · ');
};

const PreflightPanel: React.FC<PreflightPanelProps> = ({
  pages,
  issues,
  onClose,
  onGoToIssue,
  onFixIssue,
  onContinue,
  onContinueToExportReview,
  focusedPageId,
  fixingIssueKey
}) => {
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const summary = summarizePreflight(issues);
  const pageOrder = useMemo(
    () => new Map(pages.map((page, index) => [page.id, index])),
    [pages]
  );
  const groups = useMemo<PageIssueGroup[]>(() => {
    const grouped = new Map<string, PreflightIssue[]>();
    for (const current of issues) {
      const existing = grouped.get(current.pageId) || [];
      existing.push(current);
      grouped.set(current.pageId, existing);
    }
    return Array.from(grouped.entries())
      .map(([pageId, pageIssues]) => {
        const pageIndex = pageOrder.get(pageId) ?? Number.MAX_SAFE_INTEGER;
        return {
          pageId,
          page: pageIndex === Number.MAX_SAFE_INTEGER ? undefined : pages[pageIndex],
          pageIndex,
          issues: pageIssues
        };
      })
      .sort((first, second) => {
        if (focusedPageId) {
          if (first.pageId === focusedPageId) return -1;
          if (second.pageId === focusedPageId) return 1;
        }
        return first.pageIndex - second.pageIndex;
      });
  }, [focusedPageId, issues, pageOrder, pages]);
  const canReviewForOverride = canReviewBlockedPreflightForExport(issues);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(element => !element.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;
      if (!dialogRef.current.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  const heading = summary.hasErrors
    ? 'Layout needs attention'
    : summary.hasWarnings
      ? 'Ready to export with warnings'
      : 'Layout is PDF-ready';
  const summaryText = summary.hasErrors
    ? `${summary.errorCount} blocking ${summary.errorCount === 1 ? 'issue' : 'issues'} and ${summary.warningCount} ${summary.warningCount === 1 ? 'warning' : 'warnings'} found. ${canReviewForOverride
      ? 'You can continue to final export review, where the server will decide whether these visual issues may be acknowledged.'
      : 'Export remains blocked until every hard error is fixed.'}`
    : summary.hasWarnings
      ? `${summary.warningCount} non-blocking ${summary.warningCount === 1 ? 'warning needs' : 'warnings need'} review. You may export after acknowledging them.`
      : 'Every checked page passed clipping, trim, safe-area, font, contrast, image DPI, overlap, overflow, and bleed checks.';

  return (
    <div
      className="modal-overlay preflight-overlay"
      data-testid="layout-preflight-modal"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="modal-content preflight-panel"
        data-testid="preflight-panel"
        data-error-count={summary.errorCount}
        data-warning-count={summary.warningCount}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preflight-title"
        aria-describedby="preflight-summary"
      >
        <header className="preflight-panel-header">
          <div>
            <span className={`preflight-overall-status ${summary.hasErrors ? 'error' : summary.hasWarnings ? 'warning' : 'clean'}`}>
              {summary.hasErrors ? 'Blocking issues' : summary.hasWarnings ? 'Warnings only' : 'All checks passed'}
            </span>
            <h2 id="preflight-title">
              <span aria-hidden="true">{summary.hasErrors ? '⚠️' : summary.hasWarnings ? '△' : '✅'}</span>{' '}
              {heading}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="preflight-close"
            data-testid="preflight-close"
            aria-label="Close layout check"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <p id="preflight-summary" className="preflight-summary" data-testid="preflight-summary">
          {summaryText}
        </p>

        <div className="preflight-summary-counts" aria-label="Layout check totals">
          <span className="preflight-count errors" data-testid="preflight-error-count">
            {summary.errorCount} blocking
          </span>
          <span className="preflight-count warnings" data-testid="preflight-warning-count">
            {summary.warningCount} warnings
          </span>
          <span className="preflight-count total">{summary.total} total</span>
        </div>

        {groups.length > 0 && (
          <div className="preflight-page-groups">
            {groups.map((group) => {
              const pageLabel = group.page?.pageTitle
                || (group.page ? `Page ${group.page.pageNumber}` : 'Unknown page');
              const pageErrors = group.issues.filter(issue => issue.severity === 'error').length;
              const pageWarnings = group.issues.length - pageErrors;
              return (
                <section
                  key={group.pageId}
                  className={`preflight-page-group ${group.pageId === focusedPageId ? 'is-focused' : ''}`}
                  data-testid={`preflight-page-group-${group.pageId}`}
                >
                  <header className="preflight-page-header">
                    <h3>{pageLabel}</h3>
                    <span>{pageErrors} blocking · {pageWarnings} warnings</span>
                  </header>

                  <div className="preflight-issue-list">
                    {group.issues.map((current) => {
                      const key = preflightIssueKey(current);
                      const metricText = metricsLabel(current);
                      const canPatchAutomatically = Boolean(current.autoFix?.patch)
                        && current.autoFix?.requiresReflow !== true;
                      return (
                        <article
                          key={key}
                          className={`preflight-issue-card ${current.severity}`}
                          data-testid="preflight-issue"
                          data-page-id={current.pageId}
                          data-element-id={current.elementId || ''}
                          data-severity={current.severity}
                        >
                          <div data-testid={`preflight-issue-${current.code}`}>
                            <div className="preflight-issue-heading">
                              <span className={`preflight-severity ${current.severity}`}>
                                {current.severity === 'error' ? 'Error' : 'Warning'}
                              </span>
                              <code>{current.code}</code>
                              <span className="preflight-target">{issueTargetLabel(current)}</span>
                            </div>
                            <p data-testid="preflight-reason"><strong>Reason:</strong> {current.message}</p>
                            <p className="preflight-suggestion" data-testid="preflight-suggestion">
                              <strong>Suggested fix:</strong> {current.suggestion}
                            </p>
                            {metricText && <p className="preflight-metrics">{metricText}</p>}
                            {current.autoFix?.requiresReflow && (
                              <p className="preflight-reflow-note">
                                Safe action: {current.autoFix.label}. Open the page, then use Reflow Page Text.
                              </p>
                            )}
                            <div className="preflight-issue-actions">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                data-testid="preflight-go-to-issue"
                                data-issue-code={current.code}
                                onClick={() => onGoToIssue(current)}
                              >
                                Go to page{current.elementId ? ' and element' : ''}
                              </button>
                              {canPatchAutomatically && (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  data-testid="preflight-fix-safe"
                                  data-issue-code={current.code}
                                  disabled={fixingIssueKey === key}
                                  onClick={() => void onFixIssue(current)}
                                >
                                  {fixingIssueKey === key ? 'Fixing…' : current.autoFix!.label}
                                </button>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <footer className="preflight-panel-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          {canReviewForOverride && onContinueToExportReview && (
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="preflight-review-export"
              onClick={onContinueToExportReview}
            >
              Review export anyway
            </button>
          )}
          {summary.canExport && onContinue && (
            <button
              type="button"
              className="btn btn-primary"
              data-testid="preflight-continue"
              onClick={onContinue}
            >
              {summary.hasWarnings ? 'Acknowledge warnings and continue' : 'Continue to PDF export'}
            </button>
          )}
        </footer>
      </section>
    </div>
  );
};

export default PreflightPanel;
